import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

// Cost ledger summary for the current tenant per anchor §3.7.
// Backend endpoint lands in PR-07 (#144) — until then the route 404s
// against the dev server and tests stub fetch.

export interface CostLineItem {
  category: string; // e.g. "compute", "gpu_minutes", "ai_tokens", "storage"
  current_month_usd: number;
  total_usd: number;
}

export interface TileSpend {
  tile_name: string;
  current_month_usd: number;
  total_usd: number;
  run_count: number;
}

export interface BudgetSummary {
  tenant_id: string;
  current_month_usd: number;
  total_usd: number;
  // Monthly budget cap, if the tenant has one set. Null when no cap
  // (P0 doesn't enforce caps — just observes).
  monthly_budget_usd: number | null;
  // Breakdown by category — sums to current_month_usd.
  line_items: CostLineItem[];
  // Top-N tiles by current-month spend.
  top_tiles: TileSpend[];
  // Period the current_month_usd covers (ISO YYYY-MM-DD ranges).
  period_start: string;
  period_end: string;
  // Which pricing table produced the dollar figures (U0.3, C0.5).
  // "placeholder-estimate" = the Phase-0 placeholder table — the GUI
  // renders an "estimate" badge for exactly that value. Absent on
  // older servers → no badge.
  pricing_basis?: string;
}

export const BUDGET_QUERY_KEY = ["budget", "summary"] as const;

export function useBudget() {
  return useQuery({
    queryKey: BUDGET_QUERY_KEY,
    queryFn: async (): Promise<BudgetSummary> => apiFetch<BudgetSummary>("/api/budget"),
    staleTime: 60_000,
  });
}
